import React, { useMemo, useState } from 'react';
import { Modal, Form, Button, Alert, Row, Col, Badge, Spinner } from 'react-bootstrap';
import { Shield, ExclamationTriangle, CheckCircle } from 'react-bootstrap-icons';
import { useCreateEmailAccountMutation, useGetDomainsQuery } from '../services/api';
import { getDomainPickerLabel, getRelayAccountLabel } from '../utils/relayAccountLabel';

const AddEmailAccount = ({ show, onHide, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  const [createEmailAccount] = useCreateEmailAccountMutation();

  // 🛡️ SAFE DOMAINS ONLY - Fetch real domains from AWS SES (filtered for safety)
  const { data: domainsData, isLoading: domainsLoading } = useGetDomainsQuery();
  
  const allowedDomains = useMemo(() => {
    const domains = domainsData?.data?.domains || [];

    return domains
      .map((domain) => ({
        domain: domain.domain,
        status: domain.status.toLowerCase(),
        description: `AWS SES domain - ${domain.status}`,
        accountId: domain.accountId,
        region: domain.region || 'us-east-1',
        relayLabel: getRelayAccountLabel(domain),
        pickerLabel: getDomainPickerLabel(domain),
      }))
      .sort((a, b) => a.domain.localeCompare(b.domain, undefined, { sensitivity: 'base' }));
  }, [domainsData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsCreating(true);

    try {
      const result = await createEmailAccount({
        email: email, // Send just the username part
        domain: selectedDomain
      }).unwrap();

      if (result.success) {
        // Important safety behavior: adding an email account must not mutate domain DNS/MX.
        // Inbound provider and MX are managed only from the Domains inbound-provider flow.
        onSuccess(result);
        handleClose();
      }
    } catch (err) {
      setError(err.data?.message || 'Failed to create email account');
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setSelectedDomain('');
    setError('');
    onHide();
  };

  const getDomainStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return <Badge bg="success"><CheckCircle className="me-1" />Verified</Badge>;
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <Shield className="me-2" />
          Add Email Account
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning" className="mb-4">
          <ExclamationTriangle className="me-2" />
          <strong>🛡️ DOMAIN SAFETY MODE</strong><br/>
          Only pre-approved domains can be used to prevent affecting existing infrastructure.
        </Alert>

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <Form.Text className="text-muted">
                  Enter the username part (before @)
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Domain</Form.Label>
                {domainsLoading ? (
                  <div className="d-flex align-items-center">
                    <Spinner size="sm" className="me-2" />
                    <span>Loading domains from AWS SES...</span>
                  </div>
                ) : (
                  <Form.Select
                    value={selectedDomain}
                    onChange={(e) => setSelectedDomain(e.target.value)}
                    required
                  >
                    <option value="">Select a domain</option>
                    {allowedDomains.map((domainInfo) => (
                      <option key={domainInfo.domain} value={domainInfo.domain}>
                        {domainInfo.pickerLabel} ({domainInfo.status})
                      </option>
                    ))}
                  </Form.Select>
                )}
                <Form.Text className="text-muted">
                  Only verified domains from AWS SES are available
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {selectedDomain && (
            <Alert variant="info" className="mb-3">
              <strong>Selected Domain:</strong> {selectedDomain}<br/>
              <strong>Full Email:</strong> {email}@{selectedDomain}<br/>
              {allowedDomains.find(d => d.domain === selectedDomain)?.relayLabel && (
                <>
                  <strong>Relay Account:</strong> {allowedDomains.find(d => d.domain === selectedDomain)?.relayLabel}<br/>
                </>
              )}
              <strong>Status:</strong> {getDomainStatusBadge(allowedDomains.find(d => d.domain === selectedDomain)?.status)}
            </Alert>
          )}

          <Row className="mb-3">
            <Col>
              <h6>Available Domains from AWS SES:</h6>
              {domainsLoading ? (
                <div className="text-center p-3">
                  <Spinner size="sm" className="me-2" />
                  Loading domains from AWS SES...
                </div>
              ) : allowedDomains.length > 0 ? (
                allowedDomains.map((domainInfo) => (
                  <div key={domainInfo.domain} className="d-flex justify-content-between align-items-center mb-2 p-2 border rounded">
                    <div>
                      <strong>{domainInfo.domain}</strong><br/>
                      <small className="text-muted">{domainInfo.description}</small>
                      {domainInfo.relayLabel && (
                        <>
                          <br />
                          <small className="text-muted">{domainInfo.relayLabel}</small>
                        </>
                      )}
                    </div>
                    <div>
                      {getDomainStatusBadge(domainInfo.status)}
                    </div>
                  </div>
                ))
              ) : (
                <Alert variant="warning">
                  <strong>No domains found in AWS SES</strong><br/>
                  Please add and verify domains in AWS SES first.
                </Alert>
              )}
            </Col>
          </Row>

          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={isCreating || !email || !selectedDomain}
            >
              {isCreating ? 'Creating...' : 'Create Email Account'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AddEmailAccount;
